import React, { useEffect, useState, useRef } from "react";
import Navbar from "../../Components/common/Navbar";
import Footer from "../../Components/common/Footer";
import {
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faClock,
  faPaperPlane,
  faHeadset,
  faArrowRight,
  faCheckCircle,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function ContactUs() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [formRef, formInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [openFaq, setOpenFaq] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const contactCards = [
    {
      icon: faEnvelope,
      title: "Email Us",
      info: "support@alkauser.com",
      sub: "We reply within 24 hours",
      gradient: "from-primary to-primary-dark",
      href: "mailto:support@alkauser.com",
    },
    {
      icon: faPhone,
      title: "Call Us",
      info: "+1 (800) 123-4567",
      sub: "Mon – Fri, 9 am – 6 pm",
      gradient: "from-secondary to-secondary-dark",
      href: "tel:+18001234567",
    },
    {
      icon: faMapMarkerAlt,
      title: "Visit Us",
      info: "123 Alkauser Academy Lane",
      sub: "San Francisco, CA 94103",
      gradient: "from-purple-500 to-indigo-600",
      href: "#",
    },
    {
      icon: faClock,
      title: "Working Hours",
      info: "Mon – Fri: 9am – 6pm",
      sub: "Sat: 10am – 3pm",
      gradient: "from-warning to-yellow-500",
      href: "#",
    },
  ];

  const faqs = [
    { q: "How do I enroll in a course?", a: "Simply create a free account, browse our course catalog, and click 'Start Now' on any course. You'll have instant access to all lessons." },
    { q: "Are the courses really free?", a: "Yes! Most of our courses are completely free to enroll. Some premium certifications may have a fee, but the core learning content is always accessible." },
    { q: "Will I receive a certificate after completing a course?", a: "Absolutely. Upon completing a course and passing the assessment, you'll receive a globally recognized certificate you can share on LinkedIn or your resume." },
    { q: "Can I learn at my own pace?", a: "Yes — all courses are self-paced. Once enrolled, you have lifetime access to the content and can learn whenever works best for you." },
    { q: "How do I contact my instructor?", a: "Each course has a Discussion Forum where you can post questions. Instructors typically respond within 48 hours." },
  ];

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <Navbar page="contact" />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 flex items-center justify-center bg-gradient-to-br from-[#0a2e2d] via-primary-dark to-primary text-center">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full border-[36px] border-secondary/20 pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-60 h-60 rounded-full border-[24px] border-warning/15 pointer-events-none" />
        <div className="absolute top-10 left-10 w-48 h-48 bg-secondary/15 rounded-full blur-3xl animate-float pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-56 h-56 bg-warning/10 rounded-full blur-3xl animate-float-slow pointer-events-none" />

        <div className={`relative z-10 px-6 max-w-2xl mx-auto transition-all duration-1000 ease-out ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-white/85 text-sm font-medium mb-6">
            <FontAwesomeIcon icon={faHeadset} className="text-warning text-xs" />
            We're Here to Help
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-5">
            Get in Touch
            <span className="block mt-2 bg-gradient-to-r from-warning via-yellow-300 to-secondary bg-clip-text text-transparent">
              With Our Team
            </span>
          </h1>
          <p className="text-white/75 text-lg max-w-xl mx-auto leading-relaxed">
            Have a question, a partnership idea, or just want to say hello? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* ── Contact Cards ── */}
      <section className="px-6 py-16 bg-white">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactCards.map((card, i) => (
            <a
              key={i}
              href={card.href}
              className="group p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 text-center block no-underline"
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center mx-auto mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                <FontAwesomeIcon icon={card.icon} className="text-white text-xl" />
              </div>
              <h3 className="font-bold text-gray-800 text-base mb-1 group-hover:text-primary transition-colors duration-300">{card.title}</h3>
              <p className="text-gray-900 font-semibold text-sm mb-1">{card.info}</p>
              <p className="text-gray-400 text-xs">{card.sub}</p>
            </a>
          ))}
        </div>
      </section>

      {/* ── Form + Info ── */}
      <section className="px-6 py-20" style={{ backgroundColor: "#f5f0e8" }}>
        <div className="max-w-6xl mx-auto">
          <div
            ref={formRef}
            className={`grid md:grid-cols-5 gap-10 transition-all duration-700 ${formInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {/* Left info panel — 2 cols */}
            <div className="md:col-span-2 flex flex-col justify-between gap-8">
              <div>
                <span className="text-secondary font-semibold text-sm uppercase tracking-widest">Send a Message</span>
                <h2 className="text-3xl font-extrabold text-gray-900 mt-2 mb-4 leading-tight">
                  Let's Start a Conversation
                </h2>
                <p className="text-gray-500 leading-relaxed text-sm">
                  Whether you have a question about courses, pricing, certifications, or anything else — our team is ready to answer.
                </p>
              </div>

              {/* Info list */}
              <div className="space-y-5">
                {[
                  { icon: faEnvelope, label: "Email", value: "support@alkauser.com" },
                  { icon: faPhone, label: "Phone", value: "+1 (800) 123-4567" },
                  { icon: faMapMarkerAlt, label: "Location", value: "San Francisco, CA 94103" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center shrink-0 mt-0.5">
                      <FontAwesomeIcon icon={item.icon} className="text-primary text-sm" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="text-gray-800 font-semibold text-sm">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Decorative teal block */}
              <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark to-primary text-white">
                <FontAwesomeIcon icon={faHeadset} className="text-warning text-2xl mb-3" />
                <h4 className="font-bold text-lg mb-1">Live Chat Support</h4>
                <p className="text-white/75 text-sm">Available Monday to Friday, 9am – 6pm. Start a chat from any course page.</p>
              </div>
            </div>

            {/* Right form — 3 cols */}
            <div className="md:col-span-3 bg-white rounded-3xl shadow-md p-8 border border-gray-100">
              {submitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12 gap-4">
                  <div className="w-20 h-20 rounded-full bg-primary-light flex items-center justify-center mb-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-primary text-4xl" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-gray-900">Message Sent!</h3>
                  <p className="text-gray-500 max-w-xs">Thanks for reaching out. Our team will get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="text-xl font-extrabold text-gray-900 mb-6">Send Us a Message</h3>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                      <input
                        name="name" value={form.name} onChange={handleChange} required
                        placeholder="John Smith"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-800 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        name="email" type="email" value={form.email} onChange={handleChange} required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-800 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subject *</label>
                    <select
                      name="subject" value={form.subject} onChange={handleChange} required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-800 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 bg-white"
                    >
                      <option value="">Select a topic…</option>
                      <option value="course">Course Question</option>
                      <option value="account">Account & Billing</option>
                      <option value="certificate">Certificates</option>
                      <option value="partnership">Partnership</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message *</label>
                    <textarea
                      name="message" value={form.message} onChange={handleChange} required rows={5}
                      placeholder="Tell us how we can help you…"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-800 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full group flex items-center justify-center gap-3 py-4 rounded-2xl bg-primary text-white font-bold text-base hover:bg-primary-dark transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-primary/20"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    Send Message
                  </button>
                  <p className="text-center text-gray-400 text-xs mt-2">We typically respond within 24 hours.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">Got Questions?</span>
            <h2 className="text-4xl font-extrabold text-gray-800 mt-2">Frequently Asked</h2>
          </div>
          <div ref={faqRef} className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-md ${faqInView ? "animate-fadeInUp" : "opacity-0"}`}
                style={{ animationDelay: `${i * 80}ms`, animationFillMode: "both" }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className={`font-semibold text-base transition-colors duration-200 ${openFaq === i ? "text-primary" : "text-gray-800"}`}>
                    {faq.q}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-gray-400 text-sm shrink-0 ml-4 transition-transform duration-300 ${openFaq === i ? "rotate-180 text-primary" : ""}`}
                  />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaq === i ? "max-h-40" : "max-h-0"}`}>
                  <p className="px-6 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section className="relative px-6 py-16 overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-secondary/80 text-white text-center">
        <div className="absolute -top-12 -right-12 w-64 h-64 bg-warning/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-3">Still Have Questions?</h2>
          <p className="text-white/80 mb-7">Browse our full help center or start chatting with our support team right now.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@alkauser.com"
              className="group px-7 py-3 rounded-full bg-warning text-black font-bold hover:bg-yellow-300 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              Email Support
              <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#faq"
              className="px-7 py-3 rounded-full bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-all duration-300"
            >
              View FAQ
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
